import axios from "axios";
import httpClient from "@plugins/http";
import { RESOURCE_TYPE, ResourceStatus } from "@/types/lesson.ts";
import { ApiResponse } from "@apis/api.ts";
import { ContentMapTopicModel } from "@modules/content-map/content-map-types.ts";

export interface FetchLessonApiParams {
  uuid: string;
  status: ResourceStatus;
}

// Possible values for the `payloadType` field returned by the backend.
// JSON     → data already contains the JSON content directly.
// JSON_URL → data contains a presigned S3 URL; fetch it to get the JSON content.
const PAYLOAD_TYPE = {
  JSON: "JSON",
  JSON_URL: "JSON_URL",
} as const;

type PayloadType = (typeof PAYLOAD_TYPE)[keyof typeof PAYLOAD_TYPE];

// New API model returned by the backend for resource JSON endpoints.
// Single endpoints return `data` as a string; batch endpoints return `data` as string[].
interface ResourceJsonApiModel {
  payloadType: PayloadType;
  data: string | string[];
}

// Resolves a single-resource JSON API response into a plain JSON string for callers.
// `data` is either a JSON string directly (JSON) or a presigned S3 URL (JSON_URL).
const resolveResourceJson = async (
  apiCall: Promise<ApiResponse<ResourceJsonApiModel>>,
): Promise<ApiResponse<string>> => {
  const response = await apiCall;
  if (response.code !== "ok") return response;
  const { payloadType, data } = response.data;
  if (payloadType === PAYLOAD_TYPE.JSON_URL) {
    // Use plain axios (not httpClient) — S3 presigned URLs use their own auth
    // mechanism and will reject requests that include our app auth headers.
    const s3Response = await axios.get<string>(data as string, { responseType: "text" });
    return { code: "ok", data: s3Response.data };
  }
  return { code: "ok", data: data as string };
};

// Resolves a multi-resource JSON API response into an array of plain JSON strings.
// `data` is either an array of JSON strings (JSON) or an array of presigned S3 URLs (JSON_URL).
// All URLs are fetched in parallel to minimise latency.
const resolveResourceJsonMultiple = async (
  apiCall: Promise<ApiResponse<ResourceJsonApiModel>>,
): Promise<ApiResponse<string[]>> => {
  const response = await apiCall;
  if (response.code !== "ok") return response;
  const { payloadType, data } = response.data;
  const items = data as string[];
  if (payloadType === PAYLOAD_TYPE.JSON_URL) {
    // Use plain axios (not httpClient) — S3 presigned URLs use their own auth
    // mechanism and will reject requests that include our app auth headers.
    try {
      const s3Responses = await Promise.all(
        items.map((url) => axios.get<string>(url, { responseType: "text" })),
      );

      return { code: "ok", data: s3Responses.map((r) => r.data) };
    } catch (error) {
      return { code: "error", message: "Failed to fetch S3 content", error };
    }
  }
  return { code: "ok", data: items };
};

// TODO: [API] this API should be named as resource json API instead
const fetchLesson = async (params: FetchLessonApiParams): Promise<ApiResponse<string>> => {
  const timeStamp = Date.now();
  return await httpClient.get(
    `/apis/resource-json/lesson/${params.uuid}.json?status=${params.status}&t=${timeStamp}&resourceType=${RESOURCE_TYPE.LESSON}`,
  );
};

const fetchLessonImmediately = (params) => {
  const timeStamp = Date.now();
  return httpClient.get(
    `/apis/resource-json/lesson/immediately/${params.uuid}.json?status=${params.status}&t=${timeStamp}`,
  );
};

const fetchQuizImmediately = (params) => {
  return httpClient.get(
    `/apis/resource-json/immediately/${params.uuid}.json?resourceType=${RESOURCE_TYPE.LESSON}`,
  );
};

const fetchQuestion = (params) => {
  const queryParams = getQueryParams(params, RESOURCE_TYPE.QUESTION);
  return resolveResourceJson(
    httpClient.get(`/apis/resource-json/${params.uuid}.json`, { params: queryParams }),
  );
};

// The batch endpoint returns an array of JSON strings (JSON) or presigned URLs (JSON_URL).
const fetchQuestions = (params) => {
  return resolveResourceJsonMultiple(
    httpClient.get(`/apis/resource-json/multiple`, {
      params: { ...params, resourceType: RESOURCE_TYPE.QUESTION },
    }),
  );
};

const fetchPoll = (params) => {
  const queryParams = getQueryParams(params, RESOURCE_TYPE.POLL);
  return resolveResourceJson(
    httpClient.get(`/apis/resource-json/${params.uuid}.json`, { params: queryParams }),
  );
};

const fetchActivityJson = (params) => {
  return httpClient.get(`/apis/resource-json/activity/${params.id}.json`);
};

const fetchContentMapTopicsJson = async (
  uuid: string,
): Promise<ApiResponse<ContentMapTopicModel[]>> => {
  const response = await resolveResourceJson(
    httpClient.get(`/apis/resource-json/contentmap-topics/${uuid}.json`),
  );
  if (response.code !== "ok") return response;
  try {
    return { code: "ok", data: JSON.parse(response.data) };
  } catch (error) {
    return { code: "error", message: "Failed to parse JSON response", error };
  }
};

const getQueryParams = (params, resourceType) => {
  const queryParams = new URLSearchParams();
  if (params.contextType) {
    queryParams.append("contextType", params.contextType);
  }
  if (params.contextUuid) {
    queryParams.append("contextUuid", params.contextUuid);
  }
  if (resourceType) {
    queryParams.append("resourceType", resourceType);
  }
  return queryParams;
};

export default {
  fetchLesson,
  fetchPoll,
  fetchQuestion,
  fetchQuestions,
  fetchActivityJson,
  fetchContentMapTopicsJson,
  fetchLessonImmediately,
  fetchQuizImmediately,
};
